import { useState, useRef, useEffect } from "react";
import { Sparkles, Undo2, Redo2, Loader2 } from "lucide-react";
import { useRewritePost } from "../hooks/useRewritePost";
import { useAuth } from "../global-components/src";
import { isVerifyed } from "../global-components/src/utils/auth";

interface Props {
    type?: "post" | "poem" | "ads";
    getText: () => string;
    onSuccess: (text: string) => void;
    setError: (text: string) => void;
}

export function RewriteWithAIButton({ type = "post", getText, onSuccess, setError }: Props) {
    const [showDrop, setShowDrop] = useState(false);
    const dropRef = useRef<HTMLDivElement>(null);
    const { user } = useAuth();
    const { rewrite, undo, redo, canUndo, canRedo, loading, error } = useRewritePost({
        type,
        getText,
        onSuccess,
    });

    // Fecha o drop ao clicar fora
    useEffect(() => {
        setError(error || "")
    }, [error]);

    // Fecha o drop ao clicar fora
    useEffect(() => {
        function handleClickOutside(e: MouseEvent) {
            if (dropRef.current && !dropRef.current.contains(e.target as Node)) {
                setShowDrop(false);
            }
        }
        document.addEventListener("mousedown", handleClickOutside);
        return () => document.removeEventListener("mousedown", handleClickOutside);
    }, []);

    const handleRewrite = async () => {
        setError("")
        if(!getText().length){
            setError("Escreva algo para o Pacoca AI transcrever")
            return
        }
        await rewrite();
        setShowDrop(false);
    };

    return (
        <div className="ap-toolbar-icon" ref={dropRef} style={{ position: "relative", display: "inline-flex", alignItems: "center" }}>
            {/* Ícone principal — igual aos outros */}
            <div
                className="cursor-pointer icon-add-emoji"
                onClick={() => setShowDrop(prev => !prev)}
                title="Reescrever com IA"
            >
                <Sparkles className="icon-add-emoji" />
            </div>

            {/* Dropdown */}
            {showDrop && (
                <div style={{
                    position: "absolute",
                    bottom: "calc(100% + 10px)",
                    left: "50%",
                    transform: "translateX(-50%)",
                    background: "var(--background-post)",
                    border: "1px solid var(--bs-border-color, #dee2e6)",
                    borderRadius: 10,
                    padding: "12px 14px",
                    minWidth: 220,
                    boxShadow: "0 4px 20px rgba(0,0,0,.12)",
                    zIndex: 999,
                }}>
                    {/* Seta */}
                    <div style={{
                        position: "absolute",
                        bottom: -7,
                        left: "50%",
                        transform: "translateX(-50%) rotate(45deg)",
                        width: 12,
                        height: 12,
                        background: "var(--background-post)",
                        border: "1px solid var(--bs-border-color, #dee2e6)",
                        borderTop: "none",
                        borderLeft: "none",
                    }} />

                    {/* Título */}
                    <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 4 }}>
                        <Sparkles size={14} style={{ color: "var(--bs-primary, #0d6efd)", flexShrink: 0 }} />
                        <span style={{ fontWeight: 600, fontSize: 13, whiteSpace: "nowrap" }}>
                            Reescrever com o Paçoca AI
                        </span>
                    </div>

                    {!isVerifyed(user) ? (
                        /* Não verificado */
                        <p style={{ margin: 0, fontSize: 11, color: "var(--bs-secondary-color, #6c757d)" }}>
                            Você precisa ser verificado para usar essa função.
                        </p>
                    ) : (
                        /* Verificado — ações */
                        <>
                            <p style={{ margin: "0 0 10px", fontSize: 11, color: "var(--bs-secondary-color, #6c757d)" }}>
                                A IA vai reescrever seu texto mantendo a ideia original.
                            </p>

                            <button
                                type="button"
                                disabled={loading}
                                onClick={handleRewrite}
                                style={{
                                    width: "100%",
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                    gap: 6,
                                    padding: "6px 0",
                                    borderRadius: 7,
                                    border: "none",
                                    background: "var(--bs-primary, #0d6efd)",
                                    color: "#fff",
                                    fontSize: 13,
                                    fontWeight: 500,
                                    cursor: loading ? "not-allowed" : "pointer",
                                    opacity: loading ? 0.7 : 1,
                                }}
                            >
                                {loading
                                    ? <><Loader2 size={13} className="spin" /> Reescrevendo...</>
                                    : <><Sparkles size={13} /> Reescrever</>
                                }
                            </button>

                            {/* Desfazer / Refazer */}
                            {(canUndo || canRedo) && (
                                <div style={{ display: "flex", gap: 6, marginTop: 8 }}>
                                    <button
                                        type="button"
                                        disabled={!canUndo || loading}
                                        onClick={() => { undo(); setShowDrop(false); }}
                                        style={{
                                            flex: 1,
                                            display: "flex",
                                            alignItems: "center",
                                            justifyContent: "center",
                                            gap: 4,
                                            padding: "5px 0",
                                            borderRadius: 7,
                                            border: "1px solid var(--bs-border-color, #dee2e6)",
                                            background: "transparent",
                                            fontSize: 12,
                                            cursor: canUndo ? "pointer" : "not-allowed",
                                            opacity: canUndo ? 1 : 0.4,
                                            color: "var(--color-text-post)"
                                        }}
                                    >
                                        <Undo2 size={12} /> Desfazer
                                    </button>
                                    <button
                                        type="button"
                                        disabled={!canRedo || loading}
                                        onClick={() => { redo(); setShowDrop(false); }}
                                        style={{
                                            flex: 1,
                                            display: "flex",
                                            alignItems: "center",
                                            justifyContent: "center",
                                            gap: 4,
                                            padding: "5px 0",
                                            borderRadius: 7,
                                            border: "1px solid var(--bs-border-color, #dee2e6)",
                                            background: "transparent",
                                            fontSize: 12,
                                            cursor: canRedo ? "pointer" : "not-allowed",
                                            opacity: canRedo ? 1 : 0.4,
                                            color: "var(--color-text-post)"
                                        }}
                                    >
                                        <Redo2 size={12} /> Refazer
                                    </button>
                                </div>
                            )}
                        </>
                    )}
                </div>
            )}
        </div>
    );
}